import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

interface MergeField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'address' | 'signature' | 'email' | 'phone';
  required: boolean;
  default_value?: string;
  help_text?: string;
}

interface MergeFieldsBuilderProps {
  fields: MergeField[];
  onChange: (fields: MergeField[]) => void;
}

export function MergeFieldsBuilder({ fields, onChange }: MergeFieldsBuilderProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<MergeField | null>(null);
  const [formData, setFormData] = useState<MergeField>({
    name: '',
    label: '',
    type: 'text',
    required: false,
  });

  const openDialog = (field?: MergeField) => {
    if (field) {
      setFormData(field);
      setEditingField(field);
    } else {
      setFormData({ name: '', label: '', type: 'text', required: false });
      setEditingField(null);
    }
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!formData.name || !formData.label) return;
    if (editingField) {
      onChange(fields.map((f) => (f.name === editingField.name ? formData : f)));
    } else {
      onChange([...fields, formData]);
    }
    setDialogOpen(false);
  };

  const handleDelete = (fieldName: string) => {
    onChange(fields.filter((f) => f.name !== fieldName));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Merge Fields</CardTitle>
        <Button size="sm" onClick={() => openDialog()}>
          <Plus className="h-3 w-3 mr-1" />
          Add Field
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {fields.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No merge fields defined. Add fields to use in your template.
          </p>
        ) : (
          fields.map((field) => (
            <div key={field.name} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
              <div className="flex-1">
                <code className="text-sm font-mono bg-muted px-2 py-0.5 rounded">{`{{${field.name}}}`}</code>
                <p className="text-sm text-muted-foreground mt-1">{field.label}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => openDialog(field)}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(field.name)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingField ? 'Edit Merge Field' : 'Add Merge Field'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Field Name</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} disabled={!!editingField} />
            </div>
            <div>
              <Label htmlFor="label">Display Label</Label>
              <Input id="label" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} />
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="required" checked={formData.required} onCheckedChange={(checked) => setFormData({ ...formData, required: checked })} />
              <Label htmlFor="required">Required Field</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editingField ? 'Save' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
